import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  fullDecisionBody,
  isSyncDisabledError,
  parseSageSnapshot,
  sageErrorMessageFor,
  SageEntityTypeName,
  SageReconciliationMatch,
  SageReconciliationSession,
  UpdateMatchDecisionRequest,
} from '../../../../shared/services/sage-integration.model';
import { SageIntegrationService } from '../../../../shared/services/sage-integration.service';
import { ErrorGroup } from '../dashboard/errors-column.component';

export interface AivorCategory {
  categoryId: number;
  categoryName: string;
  sageItemCategoryId: number | null;
}

export type CategoryCardState =
  /** Waiting for the user: bring it in, or use an Aivor category. */
  | 'choose'
  /** An Aivor category already holds it, so its items are ready. */
  | 'in-aivor'
  /** Brought in or linked from the panel. */
  | 'done'
  /** No open category review has it; one look in Sage may find it. */
  | 'not-in-review'
  /** Looked for in Sage, and Sage doesn't have it. */
  | 'missing'
  /** The Sage items have no category at all. */
  | 'no-category';

/** One Sage item category, and the Sage items waiting for it. */
export interface CategoryCard {
  sageId: number | null;
  name: string;
  items: { matchId: number; name: string; reason: string | null }[];
  state: CategoryCardState;
  mode: 'import' | 'match';
  /** The Aivor category to use, for 'match'. */
  pick: number | null;
  /** The ItemCategory review row the panel applies. */
  row: SageReconciliationMatch | null;
  doneText: string | null;
  saving: boolean;
  error: string | null;
}

const same = (a: string | null | undefined, b: string | null | undefined) => !!a && a.trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * "Category not brought in from Sage yet": the Sage items wait in the Items review for a category
 * Aivor doesn't have. Each category is brought in or linked one by one (confirm-selected takes item
 * categories since 26/10/2026), then the items are applied again.
 */
export class CategoryFixState {
  cards: CategoryCard[] = [];
  aivorCategories: AivorCategory[] = [];
  loading = false;
  loadError = false;
  looking = false;
  looked = false;
  applying = false;
  applyError: string | null = null;

  private itemSessionId: number | null = null;
  private categorySession: SageReconciliationSession | null = null;
  private categoryRows: SageReconciliationMatch[] = [];
  private itemRows: SageReconciliationMatch[] = [];
  private group: ErrorGroup | null = null;
  // Up on every load and reset: a reply for an earlier one still says what happened and has the
  // list reloaded, but leaves the panel alone.
  private epoch = 0;

  constructor(
    private sage: SageIntegrationService,
    private changed: (message: string | null) => void,
    private syncDisabled: () => void
  ) {}

  private session(type: SageEntityTypeName) {
    // No open review is a 404; never started from here, since starting one asks Sage for everything.
    return this.sage.getSession(type).pipe(catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))));
  }

  load(group: ErrorGroup): void {
    const at = ++this.epoch;
    this.group = group;
    this.cards = [];
    this.loading = true;
    this.loadError = false;
    this.looked = false;
    this.applyError = null;
    forkJoin({
      items: this.session('Item'),
      categories: this.session('ItemCategory'),
      aivor: this.sage.getAivorRecords('ItemCategory').pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ items, categories, aivor }) => {
        if (at !== this.epoch) return;
        this.loading = false;
        this.itemSessionId = items?.session.id ?? null;
        this.itemRows = items?.matches ?? [];
        this.categorySession = categories?.session ?? null;
        this.categoryRows = categories?.matches ?? [];
        this.aivorCategories = (aivor ?? []).map((c: any) => ({ categoryId: c.categoryId, categoryName: c.categoryName ?? '', sageItemCategoryId: c.sageItemCategoryId ?? null }));
        this.build();
      },
      error: () => {
        if (at !== this.epoch) return;
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  reset(): void {
    this.epoch++;
    this.cards = [];
    this.group = null;
    this.loading = this.loadError = this.looking = this.looked = this.applying = false;
    this.applyError = null;
  }

  /** Aivor categories not yet matched to a Sage one: the only ones a Sage category can link to. */
  get linkable(): AivorCategory[] {
    return this.aivorCategories.filter((c) => c.sageItemCategoryId == null);
  }

  /** The waiting items whose category is now in Aivor. */
  get readyMatchIds(): number[] {
    return this.cards.filter((c) => c.state === 'in-aivor' || c.state === 'done').flatMap((c) => c.items.map((i) => i.matchId));
  }

  // An import problem's record id is the Sage item's code (SageImportBlockKeys).
  private build(): void {
    const previous = new Map(this.cards.map((c) => [c.sageId, c]));
    const bySage = new Map<number | null, CategoryCard['items']>();
    for (const record of this.group?.records ?? []) {
      const row = this.itemRows.find((m) => m.sageRecordId === record.recordId || same(parseSageSnapshot(m.sageRecordSnapshot)?.Code, record.recordId));
      if (!row) continue;
      const raw = parseSageSnapshot(row.sageRecordSnapshot);
      const sageId: number | null = raw?.Category?.ID ?? null;
      const list = bySage.get(sageId) ?? [];
      if (!list.some((i) => i.matchId === row.id)) list.push({ matchId: row.id, name: raw?.Description || record.name, reason: null });
      bySage.set(sageId, list);
    }
    this.cards = [...bySage.entries()].map(([sageId, items]) => this.card(sageId, items, previous.get(sageId)));
  }

  private card(sageId: number | null, items: CategoryCard['items'], before?: CategoryCard): CategoryCard {
    const row = sageId == null ? null : this.categoryRows.find((m) => m.sageRecordId === String(sageId)) ?? null;
    const name = (row && parseSageSnapshot(row.sageRecordSnapshot)?.Description) || before?.name || (sageId == null ? 'No category' : `Sage category ${sageId}`);
    const base = { sageId, name, items, row, saving: false, error: null, doneText: null, mode: 'import' as const, pick: null };
    if (before && (before.state === 'done' || before.state === 'missing')) return { ...before, items };
    if (sageId == null) return { ...base, state: 'no-category' };
    const inAivor = this.aivorCategories.find((c) => c.sageItemCategoryId === sageId);
    if (inAivor) return { ...base, state: 'in-aivor', doneText: `Already in Aivor as "${inAivor.categoryName}"` };
    if (!row) return { ...base, state: this.looked ? 'missing' : 'not-in-review' };
    // A same-name category is the likeliest match, and importing would make a second one.
    const sameName = this.linkable.find((c) => same(c.categoryName, name));
    return sameName ? { ...base, state: 'choose', mode: 'match', pick: sameName.categoryId } : { ...base, state: 'choose' };
  }

  bringIn(card: CategoryCard): void {
    if (!card.row) return;
    this.apply(card, fullDecisionBody(card.row, { decision: 'ImportToAivor', resolvedAivorRecordId: null }), `Brought into Aivor as "${card.name}"`);
  }

  link(card: CategoryCard): void {
    const target = this.aivorCategories.find((c) => c.categoryId === card.pick);
    if (!card.row || !target) return;
    this.apply(card, fullDecisionBody(card.row, { decision: 'Confirmed', resolvedAivorRecordId: target.categoryId }), `Uses "${target.categoryName}" in Aivor`);
  }

  setMode(card: CategoryCard, mode: 'import' | 'match'): void {
    this.update(card, { mode, error: null });
  }

  setPick(card: CategoryCard, pick: number | null): void {
    this.update(card, { pick, error: null });
  }

  private apply(card: CategoryCard, body: UpdateMatchDecisionRequest, doneText: string): void {
    const sessionId = this.categorySession?.id;
    const row = card.row;
    if (sessionId == null || !row || card.saving) return;
    this.update(card, { saving: true, error: null });
    const at = this.epoch;
    this.sage
      .updateMatchDecision(sessionId, row.id, body)
      .pipe(switchMap(() => this.sage.confirmSelectedMatches(sessionId, [row.id])))
      .subscribe({
        next: (result) => {
          const blocked = result.blocked.find((b) => b.matchId === row.id);
          // Already linked in Aivor: the category is there, which is what the items need.
          if (blocked && blocked.errorCode !== 'DuplicateSageLink') {
            if (at === this.epoch) this.update(card, { saving: false, error: blocked.message });
            return;
          }
          if (at === this.epoch) this.update(card, { saving: false, state: 'done', doneText: blocked ? 'Already in Aivor' : doneText });
          this.changed(null);
        },
        error: (err) => {
          if (isSyncDisabledError(err)) this.syncDisabled();
          // A refused apply leaves the PATCHed decision; put it back so a later full Apply doesn't act on it.
          this.sage.updateMatchDecision(sessionId, row.id, fullDecisionBody(row, {})).subscribe({ error: () => {} });
          if (at !== this.epoch) return;
          this.update(card, {
            saving: false,
            error: (err?.status === 409 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't save it. Try again.",
          });
        },
      });
  }

  /** One look in Sage for categories no open review has. It starts a category review, which asks Sage. */
  lookInSage(): void {
    if (this.looking || this.looked) return;
    this.looking = true;
    const at = this.epoch;
    this.sage
      .startReconciliation('ItemCategory')
      .pipe(switchMap(() => this.session('ItemCategory') as Observable<{ session: SageReconciliationSession; matches: SageReconciliationMatch[] } | null>))
      .subscribe({
        next: (categories) => {
          if (at !== this.epoch) return;
          this.looking = false;
          this.looked = true;
          this.categorySession = categories?.session ?? null;
          this.categoryRows = categories?.matches ?? [];
          this.build();
        },
        error: (err) => {
          if (isSyncDisabledError(err)) this.syncDisabled();
          if (at !== this.epoch) return;
          this.looking = false;
          this.applyError = sageErrorMessageFor(err) ?? "Couldn't look in Sage. Try again.";
        },
      });
  }

  /** Applies the waiting items whose category is now in Aivor. */
  applyItems(): void {
    const ids = this.readyMatchIds;
    if (this.applying || this.itemSessionId == null || !ids.length) return;
    this.applying = true;
    this.applyError = null;
    const at = this.epoch;
    this.sage.confirmSelectedMatches(this.itemSessionId, ids).subscribe({
      next: (result) => {
        const current = at === this.epoch;
        if (current) this.applying = false;
        const reasons = new Map(result.blocked.map((b) => [b.matchId, b.message]));
        // Applied rows leave the review, and confirm-selected refuses a batch naming one that's gone,
        // so they go even from a panel reopened since; only the current one takes the reasons.
        const applied = new Set(ids.filter((id) => !reasons.has(id)));
        this.itemRows = this.itemRows.filter((m) => !applied.has(m.id));
        this.cards = this.cards
          .map((c) => ({
            ...c,
            items: c.items.filter((i) => !applied.has(i.matchId)).map((i) => (current ? { ...i, reason: reasons.get(i.matchId) ?? null } : i)),
          }))
          .filter((c) => c.items.length);
        this.changed(result.applied > 0 ? `${result.applied} ${result.applied === 1 ? 'item' : 'items'} added to Aivor.` : null);
      },
      error: (err) => {
        if (isSyncDisabledError(err)) this.syncDisabled();
        if (at !== this.epoch) return;
        this.applying = false;
        const reason = (err?.status === 409 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't apply. Try again.";
        this.applyError = this.cards.some((c) => c.state === 'done') ? `The categories are in Aivor; now apply the items. ${reason}` : reason;
      },
    });
  }

  private update(card: CategoryCard, change: Partial<CategoryCard>): void {
    this.cards = this.cards.map((c) => (c === card || c.sageId === card.sageId ? { ...c, ...change } : c));
  }
}
